/**
 * The return URL rules (`technical.md`, Dashboard behaviour). Opening a
 * dashboard URL without a session sends the Account User to
 * `/login?returnTo={path and query}`, and logging in opens that URL instead of
 * their landing destination (`ux.md`, Flow B).
 *
 * A value that does not pass every rule is ignored and the landing applies, so
 * `returnTo` can never send anyone off this origin.
 */
export const RETURN_TO_PARAM = 'returnTo';

export const LOGIN_PATH = '/login';

/**
 * The pages shown before an account is open (`patterns/ui-patterns.md`, 2).
 * A refused session check is their normal state, so it never sends anyone from
 * one of them to log in.
 */
export const PRE_ACCOUNT_PATHS: readonly string[] = [
  LOGIN_PATH,
  '/signup',
  '/signup/check-email',
  '/verify-email',
  '/verify-email/resend',
  '/set-password',
  '/forgot-password',
  '/reset-password',
];

export function isPreAccountPath(pathname: string): boolean {
  const trimmed = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  return PRE_ACCOUNT_PATHS.includes(trimmed);
}

/**
 * The value to put in `returnTo` for the URL that was opened, or `null` when
 * there is nothing worth returning to (`/` gets none, and neither does
 * `/login`).
 */
export function returnToFor(pathAndQuery: string): string | null {
  if (pathAndQuery === '/' || pathAndQuery === '') return null;
  return safeReturnTo(pathAndQuery);
}

/** The URL to open after log in, or `null` to use the landing destination. */
export function safeReturnTo(
  value: string | null | undefined,
  origin: string = globalThis.location?.origin ?? 'http://localhost',
): string | null {
  if (value === null || value === undefined || value === '') return null;

  // One leading slash, and nothing that a browser could read as an authority.
  if (!value.startsWith('/')) return null;
  if (value.startsWith('//') || value.startsWith('/\\')) return null;

  let url: URL;
  try {
    url = new URL(value, origin);
  } catch {
    return null;
  }

  // It has to resolve to the dashboard's own origin.
  if (url.origin !== origin) return null;

  // The log in screen itself would send the Account User in a circle.
  if (url.pathname === LOGIN_PATH || url.pathname.startsWith(`${LOGIN_PATH}/`)) return null;

  // The API is not a page.
  if (url.pathname === '/api' || url.pathname.startsWith('/api/')) return null;

  return `${url.pathname}${url.search}`;
}
